/* eslint-disable react-hooks/exhaustive-deps */
import useClientQueryString from '@/hooks/use-client-query-string'
import { useNavigate, useLocation } from 'react-router-dom'
import { useRef } from 'react'
import { useAuth } from 'react-oidc-context'
import { useTrackingEvents } from '@/features/tracking-events/hooks'
import { LOGIN_TRACKED_SUB_KEY } from '@/features/authentication/constants'
import { getEnv } from '@/lib/config/runtime-env'

type LoginParams = {
  postLoginRedirectionUrl?: string
}

type UseLoginReturn = {
  onSuccessLogin: () => void
  isSigninLoading: boolean
  signIn: (params?: LoginParams) => void
  signOut: () => void
}

const useLogin = (): UseLoginReturn => {
  const navigate = useNavigate()
  const location = useLocation()
  const pathname = location.pathname
  const VITE_APP_ENV = getEnv().APP_ENV
  const postLoginRedirectionUrlRef = useRef<string>('')
  const isLocalEnv = VITE_APP_ENV === 'Local'
  const { signoutRedirect, isLoading, signinPopup, activeNavigator } = useAuth()
  const clientQs = useClientQueryString()
  const { trackLogout } = useTrackingEvents()
  postLoginRedirectionUrlRef.current =
    clientQs.getByKey('postLoginRedirectionUrl') ?? ''

  const onSuccessLogin = () => {
    if (window?.top?.location && activeNavigator !== 'signinSilent') {
      const fromRef = postLoginRedirectionUrlRef.current
      const fromUrl =
        typeof window !== 'undefined'
          ? new URLSearchParams(window.location.search).get(
              'postLoginRedirectionUrl'
            )
          : null
      const redirectUrl = (fromRef || fromUrl) ?? '/'
      window.top.location.href = redirectUrl
    }
  }

  const signIn = (params?: LoginParams) => {
    const { postLoginRedirectionUrl } = params ?? {}
    postLoginRedirectionUrlRef.current = postLoginRedirectionUrl ?? ''

    // Embedded: delegate to the host (it owns the single OIDC session + popup).
    const hostAuth = (
      window as Window & { __HOST_AUTH__?: { signIn: () => void } }
    ).__HOST_AUTH__
    if (hostAuth) {
      hostAuth.signIn()
      return
    }

    if (isLocalEnv) {
      signinPopup()
    } else if (!pathname.includes('/internal-login')) {
      navigate(
        `/internal-login?${new URLSearchParams({
          postLoginRedirectionUrl: postLoginRedirectionUrl ?? '',
        }).toString()}`
      )
    }
  }

  const signOut = () => {
    if (isLoading) return
    trackLogout()

    // Clear the GTM login dedupe marker so the next sign-in fires a fresh
    // `login` event (the host-delegated path below may redirect before this
    // app re-renders and lets PostLoginTracker reset it).
    try {
      window.sessionStorage.removeItem(LOGIN_TRACKED_SUB_KEY)
    } catch {
      // sessionStorage may be unavailable (private mode / SSR); ignore.
    }

    // Embedded: delegate logout to the host so the whole ecosystem signs out.
    const hostAuth = (
      window as Window & { __HOST_AUTH__?: { signOut: () => void } }
    ).__HOST_AUTH__
    if (hostAuth) {
      hostAuth.signOut()
      return
    }

    signoutRedirect()
  }

  return {
    onSuccessLogin,
    isSigninLoading: isLoading || pathname.includes('/internal-login'),
    signIn,
    signOut,
  }
}

export default useLogin
